import React, { useContext } from "react";
import {
  TableRow,
  TableCell,
  Checkbox,
  Typography,
  Box,
  Skeleton,
} from "@mui/material";
import IconButton from "@mui/material/IconButton";
import DownloadIcon from "../../../components/VectorIcons/DownloadIcon";
import FileView from "../../../components/VectorIcons/FileView";
import CustomTooltip from "../../../components/CustomTooltip/CustomTooltip";
import AppContext from "../../../context/appContext";
import AttachmentsContext from "../../../context/attachmentsContext";
import useDownloadAttachment from "../../../hooks/useDownloadAttachment";
import { VIEWABLE_FILE_TYPES } from "../../../shared/constants/store";
import { getFileExtensionFromFileName } from "../../../shared/utils/getFileExtensionFromFileName";
import { getLastReleasedObservation } from "../../AttachmentsScheduler/PlanningGrid/mappers/attachments";
import { addendumCellSx, getReleaseDate } from "./releasedAddendum.utils";

const regularTextSx = { fontFamily: '"Globotipo Text"' };

const ReleasedAddendumItem = ({
  file,
  title,
  contentId,
  isActive,
  onToggle,
  associationsLabel,
  isLoadingAssociations,
}) => {
  const appContext = useContext(AppContext);
  const { setFileToView } = useContext(AttachmentsContext);
  const { downloadAttachment } = useDownloadAttachment();

  const isReleased = file?.release ?? file?.releaseManagement?.channel ?? false;
  const releaseDate = getReleaseDate(file);
  const observation = getLastReleasedObservation(file?.releaseManagement);

  const canDownload = isReleased && !!file?.fileId;
  const canView = isReleased && !!file?.fileId;

  const fileExtension = getFileExtensionFromFileName(file?.fileName);

  const canViewFile =
    VIEWABLE_FILE_TYPES.includes(fileExtension) &&
    file?.fileName !== null &&
    canView;

  const fileToVisualize = {
    fileData: file,
    contentId,
    origin: "visualizador-textos-liberados",
    mode: 1,
    autoSave: false,
  };

  const fileToDownload = {
    fileData: file,
    contentId,
    origin: "textos-liberados",
    mode: 0,
  };

  return (
    <TableRow sx={{ backgroundColor: "#f7f7ff" }}>
      <TableCell sx={{ ...addendumCellSx, width: "32px" }}>
        {isReleased && (
          <Checkbox
            size="small"
            checked={isActive}
            onChange={onToggle}
            sx={{ "&.Mui-checked": { color: "#0098CD" } }}
          />
        )}
      </TableCell>

      <TableCell sx={{ ...addendumCellSx, px: 0, maxWidth: "300px" }}>
        <Box sx={{ display: "flex", flexDirection: "column", py: "16px" }}>
          <Typography
            variant="body2"
            sx={{ ...regularTextSx, color: "#43444D", fontWeight: 700 }}
          >
            {file?.fileName}
          </Typography>

          {!!observation && (
            <Typography
              variant="body2"
              sx={{ ...regularTextSx, color: "#7A7A80", fontSize: "14px" }}
            >
              {observation}
            </Typography>
          )}
        </Box>
      </TableCell>

      <TableCell sx={addendumCellSx}>
        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          {isLoadingAssociations ? (
            <Skeleton variant="text" width={80} />
          ) : (
            !!associationsLabel && (
              <Typography variant="body2" sx={regularTextSx}>
                {associationsLabel}
              </Typography>
            )
          )}
        </Box>
      </TableCell>

      <TableCell align="right" sx={{ ...addendumCellSx, pl: 4 }}>
        <Typography variant="body2" sx={regularTextSx}>
          {releaseDate &&
            appContext.formatDate(appContext.convertDatePtBr(releaseDate))}
        </Typography>
      </TableCell>

      <TableCell align="right" sx={{ ...addendumCellSx, width: 110 }}>
        {canDownload && (
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            {canViewFile ? (
              <IconButton
                size="small"
                onClick={() => setFileToView(fileToVisualize)}
                sx={{ width: 32, height: 32, marginRight: "8px" }}
              >
                <FileView color="#0098CD" />
              </IconButton>
            ) : (
              <CustomTooltip
                tipLabel={
                  <IconButton
                    size="small"
                    disabled
                    sx={{ width: 32, height: 32, marginRight: "8px" }}
                  >
                    <FileView color="#CCC" />
                  </IconButton>
                }
                dataTip="Formato não suportado ou arquivo não liberado"
                position="left"
                theme="light"
              />
            )}

            <IconButton
              size="small"
              onClick={() => downloadAttachment(fileToDownload)}
              sx={{ width: 32, height: 32 }}
            >
              <DownloadIcon />
            </IconButton>
          </Box>
        )}
      </TableCell>
    </TableRow>
  );
};

export default ReleasedAddendumItem;
